import { useState, useEffect, useCallback, useRef } from 'react'
import { versionApi } from '../services/api'

// Capacitor File Opener（自动安装 APK）
const FileOpener = window.Capacitor?.Plugins?.FileOpener
  || (window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.FileOpener)

// 当前 APP 版本号（与 package.json 保持一致）
const CURRENT_VERSION = '1.0.0'
const CURRENT_VERSION_CODE = 1

// 自动弹窗每日最多提示 1 次
const DISMISS_KEY = 'edu_update_dismiss_time'
const DISMISS_INTERVAL = 24 * 60 * 60 * 1000 // 24小时

// 手动触发弹窗的事件名
export const MANUAL_CHECK_EVENT = 'update:manual-check'
// 检查结果事件（通知 ProfilePage 显示结果）
export const CHECK_RESULT_EVENT = 'update:check-result'

export default function UpdateDialog() {
  const [show, setShow] = useState(false)
  const [updateInfo, setUpdateInfo] = useState(null)
  const [checking, setChecking] = useState(false)
  const updateInfoRef = useRef(null)

  // 显示更新弹窗
  const showUpdateDialog = useCallback((info) => {
    if (!info) return
    updateInfoRef.current = info
    setUpdateInfo(info)
    setShow(true)
  }, [])

  // 检查更新（内部方法）
  const checkUpdate = useCallback(async (isManual = false) => {
    setChecking(true)
    try {
      const res = await versionApi.check()
      if (res.versionCode > CURRENT_VERSION_CODE) {
        updateInfoRef.current = res
        setUpdateInfo(res)

        if (isManual) {
          setShow(true)
        } else {
          const lastDismiss = localStorage.getItem(DISMISS_KEY)
          if (!lastDismiss || Date.now() - Number(lastDismiss) > DISMISS_INTERVAL) {
            setShow(true)
          }
        }
        // 通知有新版本
        window.dispatchEvent(new CustomEvent(CHECK_RESULT_EVENT, {
          detail: { hasUpdate: true, version: res.version }
        }))
        return res
      } else {
        updateInfoRef.current = null
        // 通知已是最新版本
        window.dispatchEvent(new CustomEvent(CHECK_RESULT_EVENT, {
          detail: { hasUpdate: false, currentVersion: CURRENT_VERSION }
        }))
        return null
      }
    } catch (err) {
      console.log('版本检查失败:', err)
      window.dispatchEvent(new CustomEvent(CHECK_RESULT_EVENT, {
        detail: { error: true, message: '检查失败，请稍后再试' }
      }))
      return null
    } finally {
      setChecking(false)
    }
  }, [])

  // APP 启动时自动检查一次
  useEffect(() => {
    const timer = setTimeout(checkUpdate, 3000)
    return () => clearTimeout(timer)
  }, [checkUpdate])

  // 监听手动检查事件（来自 ProfilePage）
  useEffect(() => {
    const handleManualCheck = () => {
      if (updateInfoRef.current) {
        showUpdateDialog(updateInfoRef.current)
      } else {
        checkUpdate(true)
      }
    }
    window.addEventListener(MANUAL_CHECK_EVENT, handleManualCheck)
    return () => window.removeEventListener(MANUAL_CHECK_EVENT, handleManualCheck)
  }, [checkUpdate, showUpdateDialog])

  // 构建完整的下载 URL
  const getFullDownloadUrl = useCallback(() => {
    if (!updateInfoRef.current) return ''
    const url = updateInfoRef.current.downloadUrl
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }
    const apiBase = import.meta.env.VITE_API_URL || ''
    if (apiBase) {
      const domain = apiBase.replace(/\/api$/, '')
      return domain + url
    }
    return window.location.origin + url
  }, [])

  // 处理下载并自动安装
  const handleDownload = async () => {
    const downloadUrl = getFullDownloadUrl()
    if (!downloadUrl) return

    const version = updateInfoRef.current?.version || 'latest'
    const fileName = `zhixue-eduapp-${version}.apk`

    // 用 fetch 下载 APK 为 blob
    try {
      const resp = await fetch(downloadUrl)
      if (!resp.ok) throw new Error('下载失败')
      const blob = await resp.blob()

      // 将 blob 转为 base64
      const reader = new FileReader()
      const base64 = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result.split(',')[1])
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })

      // 写入 Capacitor 缓存目录
      const { Filesystem } = window.Capacitor.Plugins
      await Filesystem.writeFile({
        path: fileName,
        data: base64,
        directory: 'CACHE',
      })

      // 获取文件 URI 并打开安装
      const { uri } = await Filesystem.getUri({
        path: fileName,
        directory: 'CACHE',
      })

      await FileOpener.open({
        filePath: uri,
        contentType: 'application/vnd.android.package-archive',
        openWithDefault: true,
      })

      setShow(false)
    } catch (e) {
      console.log('自动安装失败，回退到浏览器下载:', e)
      // 回退方案：直接跳转下载
      window.location.href = downloadUrl
      setShow(false)
    }
  }

  // 处理"稍后再说"
  const handleLater = useCallback(() => {
    setShow(false)
    localStorage.setItem(DISMISS_KEY, String(Date.now()))
  }, [])

  if (!show || !updateInfo) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
      onClick={updateInfo.forceUpdate ? undefined : handleLater}
    >
      <div
        className="mx-6 rounded-3xl overflow-hidden w-full max-w-sm"
        style={{
          background: '#fff',
          boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
          animation: 'slideUp 0.3s ease-out'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* 头部 */}
        <div
          className="px-6 pt-8 pb-5 text-center"
          style={{ background: 'linear-gradient(135deg, #6C63FF, #a29bfe)' }}
        >
          <div className="w-16 h-16 rounded-2xl mx-auto mb-3 flex items-center justify-center"
               style={{ background: 'rgba(255,255,255,0.2)' }}>
            <i className="fa-solid fa-arrow-up-from-bracket text-3xl text-white" />
          </div>
          <h3 className="text-xl font-bold text-white mb-1">发现新版本</h3>
          <span className="inline-block px-3 py-1 rounded-full text-xs font-bold text-white"
                style={{ background: 'rgba(255,255,255,0.2)' }}>
            v{updateInfo.version}
          </span>
        </div>

        {/* 更新日志 */}
        <div className="px-6 py-4">
          <p className="text-xs font-bold mb-2" style={{ color: '#666' }}>更新内容：</p>
          <div
            className="text-sm leading-relaxed p-3 rounded-xl"
            style={{
              background: '#f8f8fc',
              color: '#444',
              whiteSpace: 'pre-line',
              maxHeight: '120px',
              overflowY: 'auto'
            }}
          >
            {updateInfo.updateLog}
          </div>
        </div>

        {/* 按钮 */}
        <div className="px-6 pb-6 flex flex-col gap-2.5">
          <button
            className="w-full h-12 rounded-2xl text-base font-bold text-white border-none cursor-pointer transition-all active:scale-97"
            style={{ background: 'linear-gradient(135deg, #6C63FF, #5a52d5)' }}
            onClick={handleDownload}
          >
            <i className="fa-solid fa-download mr-2" />
            立即更新
          </button>

          {!updateInfo.forceUpdate && (
            <button
              className="w-full h-10 rounded-2xl text-sm border-none cursor-pointer bg-transparent transition-all"
              style={{ color: '#999' }}
              onClick={handleLater}
            >
              稍后再说
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  )
}

// 手动触发检查的 hook
export function useAppUpdate() {
  const [hasUpdate, setHasUpdate] = useState(false)
  const [checking, setChecking] = useState(false)

  // 手动检查更新并触发弹窗，同时通过事件返回结果
  const checkAndShow = useCallback(async () => {
    setChecking(true)
    try {
      const res = await versionApi.check()
      if (res.versionCode > CURRENT_VERSION_CODE) {
        setHasUpdate(true)
        window.dispatchEvent(new CustomEvent(MANUAL_CHECK_EVENT))
        return res
      }
      setHasUpdate(false)
      return null
    } catch {
      return null
    } finally {
      setChecking(false)
    }
  }, [])

  // 获取当前版本信息
  const currentVersion = CURRENT_VERSION

  return { hasUpdate, checking, checkAndShow, currentVersion }
}
